import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { requestNotificationPermission } from '../utils/notifications';
import api from '../utils/api';
import UAEClock from './UAEClock';
import {
  LayoutDashboard,
  Users,
  Scissors,
  TrendingUp,
  TrendingDown,
  FileText,
  BarChart3,
  Calendar,
  Package,
  Wallet,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ClipboardList,
  Mail,
  AlertTriangle,
  Building2,
  PieChart,
  HardDrive,
  Send,
  Home,
  BarChart,
  MessageCircle,
  Star,
  Activity,
  BookOpen,
  Shield,
  RefreshCw,
  Trash2,
  CalendarDays,
  UserCircle2,
  Crown,
  Sparkles
} from 'lucide-react';

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, isAdmin, isGuest, guestTimeRemaining, isDemo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [pendingPriceChanges, setPendingPriceChanges] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [pendingLoyalty, setPendingLoyalty] = useState(0);

  // Request notification permission on mount
  useEffect(() => {
    if (user && !isDemo) {
      requestNotificationPermission();
      fetchUnreadNotifications();
      fetchPendingPriceChanges();
      fetchPendingLeaves();
      fetchPendingLoyalty();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadNotifications();
        fetchPendingPriceChanges();
        fetchPendingLeaves();
        fetchPendingLoyalty();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isDemo]);

  const fetchUnreadNotifications = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadNotifications(res.data.count || 0);
      if ('setAppBadge' in navigator && res.data.count > 0) {
        navigator.setAppBadge(res.data.count);
      } else if ('clearAppBadge' in navigator && res.data.count === 0) {
        navigator.clearAppBadge();
      }
    } catch (error) {
      console.error('Failed to fetch notifications count');
    }
  };

  const fetchPendingPriceChanges = async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await api.get('/services/price-changes/pending-count');
      setPendingPriceChanges(res.data?.count || 0);
    } catch (error) {
      // Silently ignore – endpoint may not be available yet
    }
  };

  const fetchPendingLeaves = async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await api.get('/leaves/pending-count');
      setPendingLeaves(res.data?.count || 0);
    } catch (error) {
      // Silently ignore – endpoint may not be available yet
    }
  };

  const fetchPendingLoyalty = async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await api.get('/loyalty/pending-count');
      setPendingLoyalty(res.data?.count || 0);
    } catch (error) {
      // Silently ignore
    }
  };

  const handleLogout = async () => {
    // Clear app badge on logout
    if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge();
    }
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard, roles: ['admin', 'barber'] },
    { path: '/booking', label: 'تسجيل طلب', icon: ClipboardList, roles: ['guest'] },
    { path: '/users', label: t('users'), icon: Users, roles: ['admin'] },
    { path: '/barbers', label: t('barbers'), icon: Scissors, roles: ['admin'] },
    { path: '/services-management', label: 'الخدمات والعروض', icon: Package, roles: ['admin', 'barber'] },
    { path: '/loyalty-admin', label: 'برنامج الولاء', icon: Crown, roles: ['admin', 'barber'] },
    { path: '/weekly-loyalty-reports', label: 'تقارير الولاء الأسبوعية', icon: BarChart3, roles: ['admin'] },
    { path: '/popup-notifications', label: 'الإشعارات المنبثقة', icon: Bell, roles: ['admin', 'barber'] },
    { path: '/my-revenues', label: 'إيراداتي', icon: Wallet, roles: ['barber'] },
    { path: '/barber-reports', label: 'تقاريري', icon: BarChart3, roles: ['barber'] },
    { path: '/revenues', label: t('revenues'), icon: TrendingUp, roles: ['admin'] },
    { path: '/expenses', label: t('expenses'), icon: TrendingDown, roles: ['admin', 'barber'] },
    { path: '/invoices', label: t('invoices'), icon: FileText, roles: ['admin', 'barber'] },
    { path: '/reports', label: t('reports'), icon: BarChart3, roles: ['admin'] },
    { path: '/appointments', label: t('appointments'), icon: Calendar, roles: ['admin'] },
    { path: '/queue', label: 'قائمة الانتظار', icon: Bell, roles: ['admin', 'barber'] },
    { path: '/inventory', label: t('inventory'), icon: Package, roles: ['admin', 'barber'] },
    { path: '/salaries', label: t('salaries'), icon: Wallet, roles: ['admin', 'barber'] },
    { path: '/fixed-commitments', label: 'الالتزامات الثابتة', icon: Building2, roles: ['admin'] },
    { path: '/analytics', label: 'تحليلات الزوار', icon: PieChart, roles: ['admin'] },
    { path: '/customer-insights', label: 'ذكاء الزبائن', icon: Sparkles, roles: ['admin'] },
    { path: '/rent-simulator', label: 'محاكاة الإيجار', icon: Building2, roles: ['admin'] },
    { path: '/daily-report', label: 'التقرير اليومي', icon: Send, roles: ['admin'] },
    { path: '/whatsapp-settings', label: 'إعدادات الواتساب', icon: MessageCircle, roles: ['admin'] },
    { path: '/notification-settings', label: 'إشعارات Push', icon: Bell, roles: ['admin', 'barber'] },
    { path: '/update-management', label: 'إدارة التحديثات', icon: RefreshCw, roles: ['admin'] },
    { path: '/app-statistics', label: 'إحصائيات التطبيق', icon: BarChart, roles: ['admin'] },
    { path: '/deleted-users', label: 'المحذوفين', icon: Trash2, roles: ['admin'] },
    { path: '/revenue-stats', label: 'إحصائيات الإيرادات', icon: BarChart, roles: ['admin'] },
    { path: '/barber-violations', label: 'مخالفات الحلاقين', icon: AlertTriangle, roles: ['admin'] },
    { path: '/data-backup', label: 'النسخ الاحتياطي', icon: HardDrive, roles: ['admin'] },
    { path: '/detailed-reports', label: 'التقارير التفصيلية', icon: FileText, roles: ['admin', 'barber'] },
    { path: '/permissions-logs', label: 'سجل الصلاحيات', icon: Shield, roles: ['admin'] },
    { path: '/employee-housing', label: 'سكن الموظفين', icon: Home, roles: ['admin', 'barber'] },
    { path: '/customers', label: 'سجل الزبائن', icon: UserCircle2, roles: ['admin', 'barber'] },
    { path: '/leaves', label: 'الإجازات والأذونات', icon: CalendarDays, roles: ['admin', 'barber'] },
    { path: '/comprehensive-reports', label: 'التقارير الشاملة', icon: BarChart3, roles: ['admin'] },
    { path: '/weekly-report', label: 'التقرير الأسبوعي', icon: FileText, roles: ['admin'] },
    { path: '/ratings', label: 'التقييمات', icon: Star, roles: ['admin', 'barber'] },
    { path: '/activity-log', label: 'سجل النشاطات', icon: Activity, roles: ['admin'] },
    { path: '/permissions', label: 'إدارة الصلاحيات', icon: Shield, roles: ['admin'] },
    { path: '/user-guide', label: 'دليل المستخدم', icon: BookOpen, roles: ['admin', 'barber', 'guest'] },
    { path: '/emails', label: 'البريد الإلكتروني', icon: Mail, roles: ['admin'] },
    { path: '/settings', label: t('settings'), icon: Settings, roles: ['admin'] },
    { path: '/profile', label: 'الملف الشخصي', icon: Users, roles: ['admin', 'barber'] },
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 ${language === 'ar' ? 'right-0' : 'left-0'} z-50 w-64 bg-card border-${language === 'ar' ? 'l' : 'r'} border-white/5 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : language === 'ar' ? 'translate-x-full' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo-gold.png" 
                  alt="KE" 
                  className="w-10 h-10 object-contain"
                />
                <div>
                  <div className="font-bold text-foreground">
                    KE Gents Saloon
                  </div>
                  <div className="text-xs text-muted-foreground">{user?.name}</div>
                </div>
              </div>
              <Button
                data-testid="close-sidebar-btn"
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="hover:bg-destructive/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const showServicesBadge = item.path === '/services-management' && pendingPriceChanges > 0;
              const showLeavesBadge = item.path === '/leaves' && pendingLeaves > 0;
              const showLoyaltyBadge = item.path === '/loyalty-admin' && pendingLoyalty > 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-[0_0_15px_rgba(212,175,55,0.3)]'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium flex-1">{item.label}</span>
                  {showServicesBadge && (
                    <span
                      data-testid="price-change-badge"
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse"
                      title={`${pendingPriceChanges} طلب تعديل سعر بانتظار الموافقة`}
                    >
                      {pendingPriceChanges > 99 ? '99+' : pendingPriceChanges}
                    </span>
                  )}
                  {showLeavesBadge && (
                    <span
                      data-testid="pending-leaves-badge"
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse"
                      title={`${pendingLeaves} طلب إجازة/إذن بانتظار الموافقة`}
                    >
                      {pendingLeaves > 99 ? '99+' : pendingLeaves}
                    </span>
                  )}
                  {showLoyaltyBadge && (
                    <span
                      data-testid="pending-loyalty-badge"
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse"
                      title={`${pendingLoyalty} عضوية بانتظار الاعتماد`}
                    >
                      {pendingLoyalty > 99 ? '99+' : pendingLoyalty}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-white/5 space-y-2">
            <Button
              data-testid="toggle-language-btn"
              onClick={toggleLanguage}
              variant="outline"
              className="w-full justify-start gap-3 border-white/10"
            >
              {language === 'ar' ? (
                <img 
                  src="https://flagcdn.com/w40/us.png" 
                  alt="English" 
                  className="w-6 h-4 object-cover rounded-sm"
                />
              ) : (
                <img 
                  src="https://flagcdn.com/w40/ae.png" 
                  alt="العربية" 
                  className="w-6 h-4 object-cover rounded-sm"
                />
              )}
              <span>{language === 'ar' ? 'English' : 'العربية'}</span>
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start gap-3 border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('logout')}</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 transition-all duration-300">
        {/* Demo Mode Banner */}
        {isDemo && (
          <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-b border-orange-500/30 px-4 py-2">
            <div className="flex items-center justify-center gap-2 text-orange-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">
                {language === 'ar' 
                  ? 'الوضع التجريبي - جميع التغييرات مؤقتة وستُحذف عند تسجيل الخروج'
                  : 'Demo Mode - All changes are temporary and will be deleted on logout'}
              </span>
            </div>
          </div>
        )}

        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-md border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="open-sidebar-btn"
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="hover:bg-primary/20"
              >
                <Menu className="w-5 h-5" />
              </Button>
              
              {/* Current Language Flag - Large (2x size) */}
              <div 
                className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-lg"
                title={language === 'ar' ? 'اللغة الحالية: العربية' : 'Current Language: English'}
              >
                {language === 'ar' ? (
                  <img 
                    src="https://flagcdn.com/w80/ae.png" 
                    alt="العربية" 
                    className="w-12 h-8 object-cover rounded shadow-md border border-white/10"
                  />
                ) : (
                  <img 
                    src="https://flagcdn.com/w80/us.png" 
                    alt="English" 
                    className="w-12 h-8 object-cover rounded shadow-md border border-white/10"
                  />
                )}
                <span className="text-xs text-primary font-medium hidden sm:block">
                  {language === 'ar' ? 'العربية' : 'English'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* UAE Clock */}
              <UAEClock compact className="hidden md:flex" />
              
              {/* Notification Bell with Badge */}
              <Link 
                to="/queue" 
                className="relative hover:bg-primary/20 p-2 rounded-lg transition-colors"
                title="التنبيهات"
              >
                <Bell className="w-5 h-5 text-muted-foreground hover:text-primary" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </Link>
              
              {/* Language Toggle in Header - Switch to other language */}
              <Button
                data-testid="header-language-btn"
                onClick={toggleLanguage}
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 hover:bg-primary/20 border border-white/10"
                title={language === 'ar' ? 'Switch to English' : 'التبديل للعربية'}
              >
                {language === 'ar' ? (
                  <>
                    <img 
                      src="https://flagcdn.com/w40/us.png" 
                      alt="English" 
                      className="w-6 h-4 object-cover rounded-sm"
                    />
                    <span className="hidden sm:inline text-sm">EN</span>
                  </>
                ) : (
                  <>
                    <img 
                      src="https://flagcdn.com/w40/ae.png" 
                      alt="العربية" 
                      className="w-6 h-4 object-cover rounded-sm"
                    />
                    <span className="hidden sm:inline text-sm">AR</span>
                  </>
                )}
              </Button>
              <div className="flex items-center gap-2 hidden sm:flex">
                <img 
                  src="/logo-gold.png"
                  alt="KE" 
                  className="w-8 h-8 object-contain"
                />
                <span className="font-bold text-primary">KE Gents Saloon</span>
              </div>
              {isDemo && (
                <div className="text-sm bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full hidden sm:block">
                  {language === 'ar' ? 'تجريبي' : 'Demo'}
                </div>
              )}
              {isGuest() && guestTimeRemaining !== null && (
                <div className="text-sm bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full">
                  ⏱️ {guestTimeRemaining} دقيقة متبقية للعميل
                </div>
              )}
              <div className="text-sm">
                <span className="text-muted-foreground">{t('role')}: </span>
                <span className="text-primary font-semibold">{t(user?.role)}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-8 lg:p-10">
          {children}
        </main>
      </div>

      {/* Overlay - Click to close sidebar */}
      {sidebarOpen && (
        <div
          data-testid="sidebar-overlay"
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;